import { computed, ref } from 'vue'

import type { ComputedRef, Ref, WritableComputedRef } from 'vue'
import type { MultiselectSearchOption } from '@shared/ui/fields'
import type { CustomerTaskCommentsFilterParams } from './filter-params'

import { useFilterDateRange } from '@shared/lib/filters'
import { useRefreshQuery } from '@shared/lib/routes'

import { useFilterDiscipline } from './filter-discipline.hook'
import { useFilterTeam } from './filter-team.hook'
import { useFilterTaskType } from './filter-task-type.hook'
import { buildQueryObject, parseFilterParams } from './filter-params'

interface CustomerTaskCommentsFiltersReturn {
  filterResetKey: Ref<number>
  createdRange: WritableComputedRef<Date[] | null>
  disciplineOptions: ComputedRef<MultiselectSearchOption[]>
  disciplineInitialValue: ComputedRef<string[]>
  handleDisciplineSelected: (selected: MultiselectSearchOption[]) => void
  teamOptions: ComputedRef<MultiselectSearchOption[]>
  teamInitialValue: ComputedRef<string[]>
  handleTeamSelected: (selected: MultiselectSearchOption[]) => void
  taskTypeOptions: ComputedRef<MultiselectSearchOption[]>
  taskTypeInitialValue: ComputedRef<string[]>
  handleTaskTypeSelected: (selected: MultiselectSearchOption[]) => void
  handleSearch: () => void
  handleClear: () => void
}

export const useCustomerTaskCommentsFilters = (): CustomerTaskCommentsFiltersReturn => {
  const route = useRoute()
  const { refreshQuery } = useRefreshQuery()

  const filterResetKey = ref(0)

  const initialParams = parseFilterParams(route.query)

  const {
    range: createdRange,
    from: createdFrom,
    to: createdTo,
  } = useFilterDateRange({
    initialFrom: initialParams.created_from,
    initialTo: initialParams.created_to,
  })

  const {
    options: disciplineOptions,
    initialValue: disciplineInitialValue,
    handleSelected: handleDisciplineSelected,
    query: disciplineQuery,
  } = useFilterDiscipline({ initial: initialParams.discipline_id })

  const {
    options: teamOptions,
    initialValue: teamInitialValue,
    handleSelected: handleTeamSelected,
    query: teamQuery,
  } = useFilterTeam({ initial: initialParams.team })

  const {
    options: taskTypeOptions,
    initialValue: taskTypeInitialValue,
    handleSelected: handleTaskTypeSelected,
    query: taskTypeQuery,
  } = useFilterTaskType({ initial: initialParams.task_type })

  const queryParams = computed<CustomerTaskCommentsFilterParams>(() => ({
    discipline_id: disciplineQuery.value,
    created_from: createdFrom.value,
    created_to: createdTo.value,
    team: teamQuery.value,
    task_type: taskTypeQuery.value,
  }))

  const handleSearch = (): void => {
    const query = buildQueryObject(queryParams.value)
    void refreshQuery(query)
  }

  const handleClear = (): void => {
    createdRange.value = null
    disciplineQuery.value = []
    teamQuery.value = []
    taskTypeQuery.value = []
    filterResetKey.value++

    void refreshQuery({})
  }

  return {
    filterResetKey,
    createdRange,
    disciplineOptions,
    disciplineInitialValue,
    handleDisciplineSelected,
    teamOptions,
    teamInitialValue,
    handleTeamSelected,
    taskTypeOptions,
    taskTypeInitialValue,
    handleTaskTypeSelected,
    handleSearch,
    handleClear,
  }
}